import * as Clipboard from "expo-clipboard";
import { useTheme } from "expo-router/react-navigation";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Platform } from "react-native";

import { Box, Center, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";

interface LinearScaleGaugeProps {
  rating: number | null;
  scale: string[];
}

export interface LinearScaleGaugeRef {
  copy: () => Promise<void>;
}

const TICK_COLOR = "hsl(220,10%,46%)";
const NEEDLE_COLOR = "#7c3aed";
const CX = 190;
const CY = 130;
const OUTER_R = 110;
const INNER_R = 70;
const ARC_GAP = 1.5;

function interpolateColor(t: number): string {
  // #ede9fe (light purple) → #7c3aed (dark purple)
  const r = Math.round(237 + (124 - 237) * t);
  const g = Math.round(233 + (58 - 233) * t);
  const b = Math.round(254 + (237 - 254) * t);
  return `rgb(${r},${g},${b})`;
}

function polarToXY(angleDeg: number, r: number) {
  const rad = ((180 + angleDeg) * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

function arcPath(startDeg: number, endDeg: number, oR: number, iR: number) {
  const s1 = polarToXY(startDeg, oR);
  const s2 = polarToXY(endDeg, oR);
  const s3 = polarToXY(endDeg, iR);
  const s4 = polarToXY(startDeg, iR);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M${s1.x},${s1.y} A${oR},${oR} 0 ${large} 1 ${s2.x},${s2.y} L${s3.x},${s3.y} A${iR},${iR} 0 ${large} 0 ${s4.x},${s4.y} Z`;
}

const LinearScaleGauge = forwardRef<LinearScaleGaugeRef, LinearScaleGaugeProps>(
  ({ rating, scale }, ref) => {
    const { i18n } = useLocalization();
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const scaleSize = scale.length;
    const startLabel = scale[0] ?? "";
    const endLabel = scale[scaleSize - 1] ?? "";

    const numSegments = scaleSize - 1;
    const segAngle = 180 / numSegments;

    const needleAngle = useMemo(() => {
      if (rating === null) return 90;
      return ((rating - 1) / numSegments) * 180;
    }, [rating, numSegments]);

    const cursorPosition = useMemo(() => {
      if (rating === null) return 50;
      return ((rating - 1) / numSegments) * 100;
    }, [rating, numSegments]);

    const segmentColors = useMemo(
      () =>
        Array.from({ length: numSegments }, (_, i) =>
          interpolateColor(numSegments === 1 ? 0.5 : i / (numSegments - 1))
        ),
      [numSegments]
    );

    const needleLen = OUTER_R - 8;
    const needleTip = polarToXY(needleAngle, needleLen);
    const needleBaseL = polarToXY(needleAngle - 90, 5);
    const needleBaseR = polarToXY(needleAngle + 90, 5);

    const handleCopy = async () => {
      if (Platform.OS !== "web" || !containerRef.current) return;
      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(containerRef.current, {
          backgroundColor: "#ffffff",
          scale: 2,
        });
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64String = (reader.result as string).split(",")[1];
            await Clipboard.setImageAsync(base64String);
          };
          reader.readAsDataURL(blob);
        }, "image/png");
      } catch (error) {
        console.error("Failed to copy gauge:", error);
      }
    };

    useImperativeHandle(ref, () => ({ copy: handleCopy }));

    if (rating === null) {
      return (
        <Center className="h-full w-full">
          <Text className="text-typography-500">
            {i18n.t("dashboardNavigator.noData")}
          </Text>
        </Center>
      );
    }

    if (Platform.OS === "web") {
      const splitLabel = (text: string): string[] => {
        if (text.length <= 22) return [text];
        const cut = text.lastIndexOf(" ", 22);
        return cut > 0 ? [text.slice(0, cut), text.slice(cut + 1)] : [text];
      };
      const startLines = splitLabel(startLabel);
      const endLines = splitLabel(endLabel);
      const labelY = CY + 8;
      const lineH = 13;

      return (
        <div ref={containerRef} style={{ width: "100%" }}>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "16px 0 8px",
              width: "100%",
            }}
          >
            <svg
              style={{ maxWidth: 420, width: "90%" }}
              viewBox={`0 0 380 ${Math.max(labelY + lineH * Math.max(startLines.length, endLines.length) + 16, CY + 56)}`}
            >
              {Array.from({ length: numSegments }, (_, i) => {
                const start = i * segAngle + (i === 0 ? 0 : ARC_GAP / 2);
                const end =
                  (i + 1) * segAngle -
                  (i === numSegments - 1 ? 0 : ARC_GAP / 2);
                return (
                  <path
                    d={arcPath(start, end, OUTER_R, INNER_R)}
                    fill={segmentColors[i]}
                    key={i}
                  />
                );
              })}

              {Array.from({ length: scaleSize }, (_, i) => {
                if (i === 0 || i === scaleSize - 1) return null;
                const angle = i * segAngle;
                const pos = polarToXY(angle, OUTER_R + 14);
                return (
                  <text
                    dominantBaseline="middle"
                    fill={TICK_COLOR}
                    fontSize="10"
                    key={i}
                    textAnchor="middle"
                    x={pos.x}
                    y={pos.y}
                  >
                    {i + 1}
                  </text>
                );
              })}

              <polygon
                fill={NEEDLE_COLOR}
                points={`${needleTip.x},${needleTip.y} ${needleBaseL.x},${needleBaseL.y} ${needleBaseR.x},${needleBaseR.y}`}
              />
              <circle cx={CX} cy={CY} fill={NEEDLE_COLOR} r="8" />
              <circle cx={CX} cy={CY} fill="white" r="4" />

              <text
                dominantBaseline="hanging"
                fill={NEEDLE_COLOR}
                fontSize="22"
                fontWeight="bold"
                textAnchor="middle"
                x={CX}
                y={CY + 14}
              >
                {rating.toFixed(1)}
              </text>

              {startLines.map((line, i) => (
                <text
                  dominantBaseline="hanging"
                  fill={TICK_COLOR}
                  fontSize="10"
                  key={i}
                  textAnchor="end"
                  x={CX - 70}
                  y={labelY + i * lineH}
                >
                  {line}
                </text>
              ))}

              {endLines.map((line, i) => (
                <text
                  dominantBaseline="hanging"
                  fill={TICK_COLOR}
                  fontSize="10"
                  key={i}
                  textAnchor="start"
                  x={CX + 70}
                  y={labelY + i * lineH}
                >
                  {line}
                </text>
              ))}
            </svg>
          </div>
        </div>
      );
    }

    // Native fallback — horizontal bar with purple fill
    return (
      <VStack className="gap-2 w-full">
        <HStack className="gap-4">
          <Box className="flex-1" />
          <Box className="flex-[4] relative h-12">
            <Box
              className="absolute bottom-0 items-center"
              style={{ left: `${cursorPosition}%`, marginLeft: -24, width: 48 }}
            >
              <Text
                className="font-bold text-2xl"
                style={{ color: NEEDLE_COLOR, marginBottom: 8 }}
              >
                {rating.toFixed(1)}
              </Text>
              <Box className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-typography-900 dark:border-t-typography-100" />
            </Box>
          </Box>
          <Box className="flex-1" />
        </HStack>

        <HStack className="items-center gap-4">
          <Text
            className="flex-1 text-right"
            style={{ color: theme.colors.text, fontSize: 14 }}
          >
            {startLabel}
          </Text>
          <Box className="flex-[4] h-10 rounded-lg overflow-hidden relative bg-primary-100">
            <Box
              className="absolute h-10 bg-primary-500"
              style={{ left: 0, width: `${cursorPosition}%` }}
            />
            <Box
              className="absolute h-10 bg-typography-900 dark:bg-typography-100"
              style={{ left: `${cursorPosition}%`, marginLeft: -2, width: 4 }}
            />
          </Box>
          <Text
            className="flex-1"
            style={{ color: theme.colors.text, fontSize: 14 }}
          >
            {endLabel}
          </Text>
        </HStack>

        <HStack className="gap-4">
          <Box className="flex-1" />
          <HStack className="flex-[4] justify-between">
            {Array.from({ length: scaleSize }, (_, idx) => (
              <Text
                key={idx}
                style={{ color: theme.colors.text, fontSize: 12 }}
              >
                {idx + 1}
              </Text>
            ))}
          </HStack>
          <Box className="flex-1" />
        </HStack>
      </VStack>
    );
  }
);

LinearScaleGauge.displayName = "LinearScaleGauge";

export default LinearScaleGauge;
